/**
 * Where the two apps sit, as far as sign-in is concerned.
 *
 * The customer app and the staff app are one build with two entry pages. A deployment
 * serves them one of two ways:
 *
 * - **One address**, staff under `/staff/` (`https://salon.example/staff/`). This is the
 *   default and needs no second DNS name. Customers sign in on the landing page, `/`;
 *   everyone who works in the salon signs in once, at `/login`, and the account's roles
 *   decide what opens. `/staff/login` is not a page: it sends people to `/login`.
 * - **A hostname of its own**, `staff.<domain>`, with the staff app at its root. This is the
 *   shape `deploy/Caddyfile`'s `STAFF_SITE_ADDRESS` block serves, and the better one when
 *   customers and staff must not share a browser session. There the staff app's own
 *   `/login` is the real form.
 *
 * Nothing here is configuration: it reads the address the page was loaded from, so the same
 * bundle works in either shape and under `scripts/serve-staff.mjs` in development.
 */

/** The staff app's prefix when it shares an address with the customer app. */
export const STAFF_PATH = '/staff';

/** The router `basepath` for the staff app: `/staff` under that path, otherwise none. */
export function staffBasepath(pathname: string): string {
  return pathname === STAFF_PATH || pathname.startsWith(`${STAFF_PATH}/`) ? STAFF_PATH : '/';
}

/** The one sign-in for staff and admins; the customer app serves it. */
export const ADMIN_LOGIN_PATH = '/login';

/**
 * Where the staff app sends someone who has no session, from wherever it is served.
 *
 * `redirect` is the route they wanted, relative to the staff app (the router's own `href`,
 * without `/staff`), so the sign-in can send them back to it.
 */
export function adminLoginHref(redirect?: string): string {
  return redirect === undefined || redirect === '/'
    ? ADMIN_LOGIN_PATH
    : `${ADMIN_LOGIN_PATH}?redirect=${encodeURIComponent(redirect)}`;
}

/** The staff app's address for a route inside it, on a shared address. */
export function staffAppHref(route: string): string {
  return `${STAFF_PATH}${route}`;
}
